import { useState } from "react"
import type { PropsWithChildren, ReactNode } from "react"
import { ChevronDownIcon, ChevronUpIcon, FilterIcon } from "../icons"

export function BusinessPageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  meta,
}: {
  eyebrow?: string
  title: string
  subtitle?: string
  actions?: ReactNode
  meta?: ReactNode
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:px-6 sm:py-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="max-w-3xl">
          {eyebrow ? (
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
              {eyebrow}
            </p>
          ) : null}
          <h1 className="mt-2 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            {title}
          </h1>
          {subtitle ? <p className="mt-2 text-sm leading-6 text-slate-500">{subtitle}</p> : null}
          {meta ? <div className="mt-3 flex flex-wrap items-center gap-3">{meta}</div> : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-stretch gap-2 sm:items-center">{actions}</div>
        ) : null}
      </div>
    </section>
  )
}

export function FilterToolbar({
  children,
  compact = false,
}: PropsWithChildren<{ compact?: boolean }>) {
  const [collapsed, setCollapsed] = useState(!compact)

  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${
        compact ? "px-4 py-4" : "px-4 py-4 sm:px-5 sm:py-5"
      }`}
    >
      {!compact ? (
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-left text-sm font-semibold text-slate-700 sm:hidden"
          onClick={() => setCollapsed((current) => !current)}
        >
          <span className="flex items-center gap-2">
            <FilterIcon className="h-4 w-4" />
            Filters
          </span>
          {collapsed ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />}
        </button>
      ) : null}
      <div
        className={`${
          compact
            ? "flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
            : collapsed
              ? "hidden sm:flex sm:flex-wrap sm:items-end sm:gap-3"
              : "mt-3 flex flex-col gap-3 sm:mt-0 sm:flex-row sm:flex-wrap sm:items-end sm:gap-3"
        }`}
      >
        {children}
      </div>
    </div>
  )
}

export function FilterField({
  label,
  children,
  widthClassName = "w-full sm:min-w-[140px]",
}: PropsWithChildren<{ label: string; widthClassName?: string }>) {
  return (
    <label className={`flex flex-col gap-1.5 ${widthClassName}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
    </label>
  )
}

export function SegmentedTabs<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: Array<{
    value: T
    label: string
    count?: number | null
    tone?: "neutral" | "info" | "warning" | "danger"
  }>
  onChange: (value: T) => void
}) {
  const toneClasses: Record<string, string> = {
    neutral: "bg-slate-100 text-slate-600",
    info: "bg-blue-100 text-blue-700",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option.value === value
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={`inline-flex min-h-10 items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors sm:px-4 ${
                active
                  ? "bg-slate-900 text-white shadow-sm"
                  : "bg-transparent text-slate-600 hover:bg-slate-100"
              }`}
            >
              <span>{option.label}</span>
              {option.count !== undefined && option.count !== null ? (
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                    active
                      ? "bg-white/15 text-white"
                      : toneClasses[option.tone ?? "neutral"]
                  }`}
                >
                  {option.count}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function MetricGrid({
  children,
  columns = "xl:grid-cols-3",
}: PropsWithChildren<{ columns?: string }>) {
  return <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${columns}`}>{children}</div>
}

export function ContentCard({
  title,
  subtitle,
  actions,
  children,
  className = "",
}: PropsWithChildren<{
  title: string
  subtitle?: string
  actions?: ReactNode
  className?: string
}>) {
  return (
    <section className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-5">
        <div>
          <h2 className="text-sm font-bold tracking-wide text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  )
}

export function FieldList({
  items,
  columns = 2,
}: {
  items: Array<{ label: string; value: ReactNode; accent?: boolean }>
  columns?: 1 | 2 | 3
}) {
  const gridColumns =
    columns === 1
      ? "grid-cols-1"
      : columns === 3
        ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
        : "grid-cols-1 md:grid-cols-2"

  return (
    <dl className={`grid gap-3 ${gridColumns}`}>
      {items.map((item) => (
        <div key={item.label} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {item.label}
          </dt>
          <dd className={`mt-1 text-sm ${item.accent ? "font-semibold text-slate-900" : "text-slate-700"}`}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function StickyActionBar({
  children,
}: PropsWithChildren) {
  return (
    <div className="admin-safe-bottom sticky bottom-0 -mx-4 mt-6 border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6">
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  )
}
